/**
 * The heart of the pinpoint board (L1): the ranked findings, as a chart and as
 * a list of cards.
 *
 * **The ranking is the argument, so it is stated rather than implied.** The
 * order comes from the backend and is never re-sorted here: findings rank by
 * `excess_hours` — person-hours of waiting above what the same form costs
 * elsewhere — which is why a 3.5× problem on a form five hundred people open
 * outranks an 18× one eight people hit. A reader who scans down the ratio column
 * and finds it out of order has been surprised by the design, so the section
 * lead says the rule and every card carries its rank.
 *
 * Each card leads with **what the finding is** — the backend's own sentence, the
 * kind, the verdict — and only then with the cost. A card that opened with a
 * number would make the reader work out what the number is about; a title makes
 * the number the answer to a question they have already been asked.
 *
 * Form follows the job (§2): a ranking of magnitudes across categories is a
 * horizontal bar sorted by value, and the composition beneath it is a
 * `SegmentBar`, which doubles as the bar chart's legend so the two agree on
 * colour. There is no donut: one number per finding split two ways would be
 * decoration.
 *
 * **Two signals travel with every finding**, because without them findings that
 * need different tickets look identical on this list:
 *
 * - **How it moves** — `trend`, up to fourteen daily medians for the finding's
 *   own cell, as a `MiniSparkline`. A form that has always been this slow and
 *   one that broke last Tuesday rank the same here; the backend separates them
 *   (`kind: "regression"`) but the eye cannot until it sees the line. Fourteen
 *   days whatever the board's lookback, so the current window can be read as
 *   unusual or as normal rather than only as itself.
 * - **Who carries it** — `concentration`, counts of how many of the people
 *   measured are slow on their own and how many of the loads are theirs, as a
 *   sentence and a `SegmentBar`. This is the board's most important correction.
 *   A company percentile is weighted by loads, so "16.9× slower than its peers"
 *   was computed over 1,055 loads of which 560 belong to eight people; read
 *   without that, the ratio sends the investigation at a company and its
 *   network when the ticket is eight desks.
 *
 * Both signals are **counts**. There is no identity in the payload and none on
 * the screen — NOVA's per-user views are separately gated and audited, and a
 * board panel gets counts.
 *
 * Both also ride the compact rows, not only the three lead cards. Most findings
 * are read as a row, and a correction only the top three carry is a correction
 * most readers never see.
 *
 * `null` is not zero in either of them (§5). An unmeasured trend renders as a
 * sentence saying so and never as a flat line at the bottom of the box; an
 * unmeasured split says "not measured" rather than showing an empty bar. Both
 * read differently from the measured zeroes that sit beside them — "no day
 * carried a load" and "nobody here is slow on their own" are findings.
 */

import { Chart } from "@norican/dataviz";
import { ClipboardCheck, Icon } from "@norican/icons";
import {
  Button,
  Card,
  cn,
  EmptyState,
  MiniSparkline,
  SegmentBar,
  Statistic,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";

import { ChartCard } from "@/components/board";
import type {
  FoConsoleFindingConcentration,
  FoConsoleFindingTrendPoint,
  FoConsoleLookback,
  FoConsolePinpointFinding,
} from "@/lib/api";
import { heatLegend } from "@/lib/charts/config";

import {
  PINPOINT_KIND_CONFIG,
  PinpointKindChip,
  pinpointKindColor,
  PinpointVerdictChip,
} from "./console-chips";
import {
  EM_DASH,
  EXCESS_HOURS_MEANING,
  formatDurationMs,
  formatExcessHours,
  formatPctWhole,
  formatRatio,
  formatShareOf,
  lookbackOption,
  NOT_MEASURED,
  PINPOINT_KIND_LABEL,
  pinpointFindingLabel,
  pinpointHoursByKind,
  type PinpointKind,
  pinpointKind,
  sharePct,
} from "./console-format";
import { PanelCard } from "./panel-card";
import { PINPOINT_FINDINGS_ANCHOR } from "./params";
import { formatCount, tooltipValue } from "./status";

/** Bars on the ranked chart. The list below carries every finding the API sent. */
const CHART_LIMIT = 12;

const TWIN_ID = "fo-pinpoint-findings";

/** The one sentence that explains the order, wherever the order could surprise. */
const RANKING_RULE =
  "Ranked by the waiting each one causes, not by how dramatic its ratio looks: a 3.5× problem on a " +
  "form five hundred people open costs more than an 18× one eight people hit.";

interface ChartRow {
  id: string;
  label: string;
  hours: number;
  /** Pre-folded, so `colorKey` always resolves to a config entry. */
  kind: PinpointKind;
  users: number;
  events: number;
  ratio: number | null;
  /** The concentration signal in words — the table twin's copy of it. */
  spread: string;
  /** Share of the cell's loads owned by the slow people, for sorting. `null` when unmeasured. */
  spreadPct: number | null;
}

interface PinpointFindingsProps {
  /** The findings to show, already filtered by the board — never re-sorted. */
  findings: FoConsolePinpointFinding[];
  /** How many the payload carried before the company filter, for the caption. */
  totalFindings: number;
  /** The company code the board is filtered to, or `null`. */
  companyFilter: string | null;
  lookback: FoConsoleLookback;
  collectedAt: string | null;
  staleAfterMs: number;
  /** Open one finding's evidence drawer (L2). */
  onOpen: (findingId: string) => void;
}

export function PinpointFindings({
  findings,
  totalFindings,
  companyFilter,
  lookback,
  collectedAt,
  staleAfterMs,
  onOpen,
}: PinpointFindingsProps) {
  const windowCaption = lookbackOption(lookback).caption;

  if (findings.length === 0) {
    return (
      <section id={PINPOINT_FINDINGS_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
        <PanelCard
          title="What to fix first"
          caption={`${windowCaption} · ranked by person-hours of waiting`}
        >
          {/* An empty list is the good outcome, and it is rendered as one. It is
              not an error, not an empty table, and not a zero: the queries ran
              and nothing cleared the floors. */}
          <EmptyState
            icon={<Icon icon={ClipboardCheck} size="lg" aria-hidden="true" />}
            title={
              totalFindings === 0
                ? "Nothing above the thresholds in this window"
                : `No finding names ${companyFilter}`
            }
            description={
              totalFindings === 0
                ? `The ranking queries ran over the ${windowCaption} and no form, company or AOS node cleared the floors the backend judges against. That is an answer, not an empty panel — nothing here is worth a ticket right now.`
                : `${formatCount(totalFindings)} findings were ranked in the ${windowCaption}, and none of them is about this company. Form-wide findings name no single company, so they sit outside this filter.`
            }
          />
        </PanelCard>
      </section>
    );
  }

  const chartRows: ChartRow[] = findings.slice(0, CHART_LIMIT).map((finding) => {
    const spread = finding.concentration ? readConcentration(finding.concentration) : null;
    return {
      id: finding.id,
      label: pinpointFindingLabel(finding),
      hours: finding.excess_hours,
      kind: pinpointKind(finding.kind),
      users: finding.users,
      events: finding.events,
      ratio: finding.ratio,
      spread: spread ? spread.headline : concentrationAbsence(pinpointKind(finding.kind)).short,
      spreadPct: spread ? spread.loadsPct : null,
    };
  });
  const composition = pinpointHoursByKind(findings);

  return (
    <section id={PINPOINT_FINDINGS_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <ChartCard<ChartRow>
        title="Waiting caused, worst first"
        caption={`${formatCount(findings.length)}${
          findings.length !== totalFindings ? ` of ${formatCount(totalFindings)}` : ""
        } findings · ${windowCaption} · click a bar for the evidence`}
        since={collectedAt}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: TWIN_ID,
          ariaLabel: "Person-hours of waiting per finding",
          data: chartRows,
          getRowId: (row) => row.id,
          columns: [
            { id: "finding", header: "Finding", accessor: (row) => row.label },
            {
              id: "kind",
              header: "Kind",
              accessor: (row) => PINPOINT_KIND_LABEL[row.kind],
            },
            {
              id: "hours",
              header: "Waiting",
              align: "end",
              accessor: (row) => row.hours,
              cell: (row) => <span className="tabular-nums">{formatExcessHours(row.hours)}</span>,
            },
            {
              id: "users",
              header: "People",
              align: "end",
              accessor: (row) => row.users,
              cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
            },
            {
              id: "events",
              header: "Loads",
              align: "end",
              accessor: (row) => row.events,
              cell: (row) => <span className="tabular-nums">{formatCount(row.events)}</span>,
            },
            {
              id: "ratio",
              header: "Against peers",
              align: "end",
              accessor: (row) => row.ratio,
              cell: (row) => <span className="tabular-nums">{formatRatio(row.ratio)}</span>,
            },
            {
              // The ratio column above is the one this column exists to
              // qualify: sorting by the share of loads the slow people own puts
              // the findings whose ratio describes a handful of desks at one
              // end and the ones that really are company-wide at the other.
              id: "spread",
              header: "Who is slow",
              align: "start",
              accessor: (row) => row.spreadPct,
              cell: (row) => <span>{row.spread}</span>,
            },
          ],
        }}
      >
        <div className="flex flex-col gap-3">
          <Chart.Container
            config={PINPOINT_KIND_CONFIG}
            className="h-80"
            aria-label={`Person-hours of waiting per finding, worst first, ${windowCaption}`}
          >
            <Chart.BarChart
              data={chartRows}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              {/* The unit on the axis, not only in the caption: a bare "12" on a
                  board that also prints milliseconds is an invitation to read
                  the wrong scale. */}
              <Chart.XAxis
                {...Chart.axisProps}
                type="number"
                tickFormatter={(value) => `${value} h`}
              />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={190} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    rows={(datum: ChartRow) => [
                      { label: "Waiting", value: formatExcessHours(tooltipValue(datum.hours)) },
                      { label: "Kind", value: PINPOINT_KIND_LABEL[datum.kind] },
                      { label: "People", value: formatCount(datum.users) },
                      { label: "Loads", value: formatCount(datum.events) },
                      { label: "Against peers", value: formatRatio(datum.ratio) },
                    ]}
                  />
                }
              />
              {/* Per-datum colour through the kit's `colorKey`, not a hand-mapped
                  `Chart.Cell` array: the DS wrapper resolves each bar from the
                  container config (Recharts' own `Cell` is deprecated), and
                  `kind` is pre-folded, so a kind the backend adds later paints
                  from the "Other" entry instead of coming out unfilled. */}
              <Chart.Bar
                {...Chart.seriesDefaults}
                data={chartRows}
                dataKey="hours"
                colorKey="kind"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={(_, index) => {
                  const row = chartRows[index];
                  if (row) {
                    onOpen(row.id);
                  }
                }}
              />
            </Chart.BarChart>
          </Chart.Container>

          {/* The chart's legend and its composition in one control: the same
              fixed kind colours, with the words and the share beside each. */}
          <SegmentBar
            size="sm"
            showLegend
            segments={composition.map((slice) => ({
              value: slice.hours,
              label: PINPOINT_KIND_LABEL[slice.kind],
              color: pinpointKindColor(slice.kind),
              pattern: slice.kind === "other" ? "striped" : "solid",
            }))}
            aria-label="Person-hours of waiting split by the kind of finding"
          />

          <Typography variant="caption" tone="muted">
            {findings.length > CHART_LIMIT
              ? `The ${CHART_LIMIT} worst findings are charted; all ${formatCount(findings.length)} are listed below. `
              : ""}
            {RANKING_RULE}
          </Typography>
        </div>
      </ChartCard>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            Every finding, in the order it is worth fixing
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {formatCount(findings.length)} findings · {windowCaption}
          </Typography>
        </div>
        {/* The unit spelled out once, where the list that ranks by it starts. */}
        <Typography variant="body-sm" tone="muted">
          {EXCESS_HOURS_MEANING}
        </Typography>
        {/* The top few in full, the rest as rows.
            Ranking is the point of this list, and a full card for every finding
            worked against it: six near-identical blocks of title, chips, six
            figures, a paragraph and a button read as a wall rather than as an
            order, and the reader has to compare paragraphs to see which is
            worst. The leaders keep their card because they are the ones anybody
            acts on; below them a row carries the same fields at a glance and
            opens the same drawer. Nothing is dropped and nothing is hidden. */}
        {findings.slice(0, LEAD_CARDS).map((finding, index) => (
          <FindingCard
            key={finding.id}
            rank={index + 1}
            finding={finding}
            onOpen={() => onOpen(finding.id)}
          />
        ))}
        {findings.length > LEAD_CARDS ? (
          <div className="border-border overflow-hidden rounded-md border">
            {findings.slice(LEAD_CARDS).map((finding, index) => (
              <FindingRow
                key={finding.id}
                rank={LEAD_CARDS + index + 1}
                finding={finding}
                onOpen={() => onOpen(finding.id)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * How many findings get a full card before the list turns into rows.
 *
 * Three is what fits above the fold beside the chart, and in practice the
 * ranking's whole value is in its head: the fourth-worst finding is read to
 * decide whether it is worth a ticket, not to be studied.
 */
const LEAD_CARDS = 3;

/**
 * One finding past the leaders: the same fields, the same drawer — and the same
 * two signals as the cards above it.
 *
 * Two lines, not one. The first is the finding and its cost; the second is the
 * concentration, which is not an extra column but a correction to the first
 * line's own numbers, and dropping it here would leave it on the three findings
 * nobody has to be convinced about.
 */
function FindingRow({ rank, finding, onOpen }: FindingCardProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="border-border hover:bg-bg-subtle flex w-full flex-col gap-1 border-t px-4 py-2.5 text-left first:border-t-0 focus-visible:outline-2"
    >
      <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-1">
        <Typography
          variant="caption"
          tone="muted"
          as="span"
          className="w-6 font-semibold tabular-nums"
        >
          #{rank}
        </Typography>
        <Typography variant="body-sm" as="span" className="min-w-0 flex-1 truncate font-medium">
          {finding.title}
        </Typography>
        <PinpointKindChip kind={finding.kind} />
        <Typography variant="caption" tone="muted" as="span" className="tabular-nums">
          {formatCount(finding.users)} people · {formatCount(finding.events)} loads
        </Typography>
        <TrendCell finding={finding} />
        <Typography
          variant="body-sm"
          as="span"
          className="w-20 text-right font-semibold tabular-nums"
        >
          {formatExcessHours(finding.excess_hours)}
        </Typography>
      </div>
      {/* Indented to the title's column, so the sentence reads as belonging to
          this finding rather than to the list. */}
      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 pl-10">
        <ConcentrationLine finding={finding} />
      </div>
    </button>
  );
}

interface FindingCardProps {
  rank: number;
  finding: FoConsolePinpointFinding;
  onOpen: () => void;
}

function FindingCard({ rank, finding, onOpen }: FindingCardProps) {
  return (
    <Card variant="outline">
      <Card.Body>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div className="flex flex-col gap-2 lg:col-span-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Typography
                variant="caption"
                tone="muted"
                as="span"
                className="font-semibold tabular-nums"
              >
                #{rank}
              </Typography>
              {/* The title is the door: what the finding IS, and the way into
                  its evidence. A separate button carries the same target for
                  anyone scanning the right-hand column. */}
              <button
                type="button"
                className="text-fg rounded text-left text-base font-semibold hover:underline focus-visible:outline-2"
                onClick={onOpen}
              >
                {finding.title}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <PinpointKindChip kind={finding.kind} />
              <PinpointVerdictChip verdict={finding.verdict} />
              {finding.company !== "" && (
                <Typography variant="caption" tone="muted" as="span">
                  {finding.company_name} ({finding.company})
                </Typography>
              )}
            </div>
            <dl className="text-fg-muted flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <Fact label="People" value={formatCount(finding.users)} />
              <Fact label="Loads" value={formatCount(finding.events)} />
              <Fact label="Median" value={formatDurationMs(finding.p50_ms)} />
              <Fact
                label="Peer median"
                value={
                  finding.peer_p50_ms == null
                    ? // `form_all` has no faster peer to point at; saying so is
                      // more useful than an em dash the reader has to decode.
                      "no faster peer"
                    : formatDurationMs(finding.peer_p50_ms)
                }
              />
              <Fact label="Against peers" value={formatRatio(finding.ratio)} />
              <Fact label="P95" value={formatDurationMs(finding.p95_ms)} />
            </dl>
            {/* The two signals sit directly under the figures they qualify: the
                medians and the peer ratio above are load-weighted over the
                board's window, and these say how the same cell has moved over
                fourteen days and how few people those medians may describe.
                Above "Next step", because they are what decides whether the
                next step is the right one. */}
            <div className="bg-bg-subtle grid grid-cols-1 gap-4 rounded-md p-3 sm:grid-cols-2">
              <TrendBlock finding={finding} />
              <ConcentrationBlock finding={finding} />
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <Typography variant="body-sm" as="span" className="font-medium">
                Next step:
              </Typography>
              <Typography variant="body-sm" tone="muted" as="span">
                {finding.next_step}
              </Typography>
            </div>
          </div>

          <div className="flex flex-col items-start gap-3 lg:items-end">
            <Statistic
              label="Waiting caused"
              value={formatExcessHours(finding.excess_hours)}
              tone={finding.excess_hours > 0 ? "warning" : "neutral"}
              size="lg"
              caption={`${formatCount(finding.evidence.activity_ids.length)} activity ids captured`}
            />
            <Button variant="outline" tone="neutral" size="sm" onClick={onOpen}>
              Evidence
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

/* ------------------------------------------------------------------------ *
 * The two per-finding signals: how it moves, and who carries it
 * ------------------------------------------------------------------------ */

/** Days the backend sends with every finding, whatever the board's lookback. */
const TREND_DAYS = 14;

/** Loads one person needs before the backend gives them a median of their own. */
const MEDIAN_MIN_LOADS = 3;

/**
 * At or below this share of the **measured** people, the slow group is a
 * minority worth pointing at rather than a description of everybody.
 */
const MINORITY_PCT = 50;

/** Said once, so a row and a card cannot word the same signal two ways. */
const TREND_LABEL = `Last ${TREND_DAYS} days`;
const CONCENTRATION_LABEL = "Who is slow";

const NEUTRAL_SCALE = heatLegend("neutral");

/**
 * The loads split.
 *
 * Warning is the reserved "degraded" tone and this is exactly that — loads that
 * went to somebody whose own median is above the slow bar. The remainder is not
 * "good", merely the rest, so it wears the neutral scale rather than success
 * (design rules §3.2): a bar that painted the majority green would be scoring
 * the people, and the finding is about the form.
 */
const LOADS_COLOR = {
  slow: "var(--norican-warning)",
  rest: NEUTRAL_SCALE[1] ?? "var(--norican-bg-muted)",
} as const;

/** One `SegmentBar` slice. The DS type is only exported under a deprecated alias. */
interface LoadSegment {
  value: number;
  label: string;
  color: string;
}

/** What a missing signal is, in the row's words and in the card's. */
interface SignalAbsence {
  short: string;
  long: string;
}

/**
 * Why there is no split across people.
 *
 * `concentration` is null for three reasons and two of them are knowable from
 * the finding itself: `form_all` and `aos_node` have no single (form, company)
 * cell to count over, so nothing could have been counted. Everything else is a
 * measurement that did not happen — the cell sat outside the signal set, or the
 * query did not answer. None of the three is "nobody is slow", and none of them
 * may render as an empty bar (§5).
 */
function concentrationAbsence(kind: PinpointKind): SignalAbsence {
  if (kind === "form_all") {
    return {
      short: "spans every company — no single cell to split",
      long: `A split across people needs one form in one company, and this finding spans every company that opens the form. Nothing was counted here, which is not the same as nobody being slow.`,
    };
  }
  if (kind === "aos_node") {
    return {
      short: "an AOS node, not a form — no cell to split",
      long: `A split across people needs one form in one company, and this finding names an AOS instance rather than a form. Nothing was counted here, which is not the same as nobody being slow.`,
    };
  }
  return {
    short: NOT_MEASURED,
    long: "The split across people was not measured for this finding — the cell sat outside the signal set, or its query did not answer. Unmeasured, not evenly spread.",
  };
}

/** Why there is no daily line. Same three causes, said about the trend. */
function trendAbsence(kind: PinpointKind): SignalAbsence {
  if (kind === "form_all") {
    return {
      short: "spans every company — no single cell to follow",
      long: "A daily line needs one form in one company, and this finding spans every company that opens the form.",
    };
  }
  if (kind === "aos_node") {
    return {
      short: "an AOS node, not a form — no cell to follow",
      long: "A daily line needs one form in one company, and this finding names an AOS instance rather than a form.",
    };
  }
  return {
    short: NOT_MEASURED,
    long: `The ${TREND_DAYS}-day line was not measured for this finding — the cell sat outside the signal set, or its query did not answer. Unmeasured, not flat.`,
  };
}

type ConcentrationShape =
  /** A minority of the measured people, and the loads they own. */
  | "minority"
  /** Most or all of them — the cost belongs to the form, not to a few desks. */
  | "widespread"
  /** Measured, and nobody's own median clears the bar. A zero, and a real one. */
  | "no_slow"
  /** Nobody opened it often enough to have a median of their own. */
  | "below_floor"
  /** Parts larger than their whole — a broken input, said rather than drawn. */
  | "impossible";

interface ConcentrationRead {
  shape: ConcentrationShape;
  /** The whole signal in one line: "8 of 18 measured people · 53 % of the loads". */
  headline: string;
  /** The correction, spelled out. The card prints it; the row carries it on hover. */
  detail: string;
  /** Accessible name for the bar. */
  barLabel: string;
  /** Share of loads owned by the slow people, 0–100, or `null` with no denominator. */
  loadsPct: number | null;
  /** The loads split, or `null` when the counts cannot honestly be drawn. */
  segments: LoadSegment[] | null;
}

/**
 * The concentration counts as the sentence the row and the card both print.
 *
 * The denominator is `users_measured`, never `users`: a person needs three
 * loads before they get a median of their own, so on the measured USL cell the
 * claim is eight of eighteen people, not eight of twenty-seven. Dividing by
 * `users` understates by however many people opened the form twice, which is
 * the flattering direction — the whole reason the backend sends the field.
 * `users` is still named in `detail`, because "how many people touch this form"
 * is the other half of the reader's question.
 */
function readConcentration(concentration: FoConsoleFindingConcentration): ConcentrationRead {
  const people = concentration.users;
  const measured = concentration.users_measured;
  const slow = concentration.users_slow;
  const loads = concentration.loads;
  const loadsSlow = concentration.loads_slow;

  // A part larger than its whole is not a share, it is a broken input — and on
  // a panel whose point is honesty about partial data, the one thing that must
  // never be printed is a confident percentage computed from it.
  if (slow > measured || measured > people || loadsSlow > loads) {
    return {
      shape: "impossible",
      headline: formatShareOf(loadsSlow, loads),
      detail: `The counts do not add up — ${formatCount(slow)} slow of ${formatCount(measured)} measured of ${formatCount(people)} people, ${formatCount(loadsSlow)} of ${formatCount(loads)} loads — so nothing is drawn from them.`,
      barLabel: "",
      loadsPct: null,
      segments: null,
    };
  }

  const loadsPct = sharePct(loadsSlow, loads);
  const peoplePct = sharePct(slow, measured);
  const segments: LoadSegment[] | null =
    loads > 0
      ? [
          { value: loadsSlow, label: "Above the bar", color: LOADS_COLOR.slow },
          { value: loads - loadsSlow, label: "Everyone else", color: LOADS_COLOR.rest },
        ]
      : null;
  const barLabel = `Form loads by whether the person's own median is above the slow bar: ${formatShareOf(loadsSlow, loads)} belong to the ${formatCount(slow)} people above it.`;
  // Said the same way in every branch, and without the "27 of 27 of them"
  // clumsiness on a cell where everybody cleared the floor.
  const measuredClause =
    measured === people
      ? `All ${formatCount(people)} people who opened this form did so at least ${MEDIAN_MIN_LOADS} times, so each has a median of their own.`
      : `${formatCount(people)} people opened this form; ${formatCount(measured)} of them the ${MEDIAN_MIN_LOADS} times a median of their own needs.`;

  if (measured === 0) {
    return {
      shape: "below_floor",
      headline: `Nobody opened it ${MEDIAN_MIN_LOADS} times, so nobody has a median of their own`,
      detail: `${formatCount(people)} people opened this form in this window, none of them the ${MEDIAN_MIN_LOADS} times a median of their own needs. Whether the waiting sits with a few of them or with all of them is measurable only above that floor.`,
      barLabel,
      loadsPct,
      // Drawing the split here would show 0 % above the bar, which reads as
      // "nobody is slow" when the truth is that nobody was measured.
      segments: null,
    };
  }

  if (slow === 0) {
    return {
      shape: "no_slow",
      headline: `None of the ${formatCount(measured)} people measured is slow on their own`,
      detail: `${measuredClause} None of those medians is above the slow bar, and that zero was measured — no individual stands out here, so whatever this costs is not carried by a subset of the people.`,
      barLabel,
      loadsPct,
      segments,
    };
  }

  const headline = `${formatCount(slow)} of ${formatCount(measured)} measured people · ${formatPctWhole(loadsPct)} of the loads`;

  if (peoplePct != null && peoplePct <= MINORITY_PCT) {
    return {
      shape: "minority",
      headline,
      detail: `${measuredClause} A company percentile is weighted by loads, so the medians and the peer ratio above are largely these ${formatCount(slow)} people's experience rather than the company's.`,
      barLabel,
      loadsPct,
      segments,
    };
  }

  return {
    shape: "widespread",
    headline,
    detail: `${measuredClause} ${formatCount(slow)} of those are slow on their own, so the waiting is spread across the people who use it rather than carried by a handful of them.`,
    barLabel,
    loadsPct,
    segments,
  };
}

type TrendRead =
  /** Nothing to draw — with the reason, which is never "flat". */
  | { state: "none"; caption: string }
  | { state: "line"; values: number[]; short: string; label: string; caption: string };

const DAY_MS = 86_400_000;

const DAY_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

/** "15 Aug", in UTC — the bins are UTC days, so the label must not drift with the reader. */
function formatDay(day: string): string {
  const ms = Date.parse(day);
  return Number.isFinite(ms) ? DAY_FORMAT.format(ms) : day.slice(0, 10);
}

/** Calendar days from the first bin to the last, inclusive; the point count when they do not parse. */
function daySpan(from: string, to: string, fallback: number): number {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return fallback;
  }
  return Math.round((end - start) / DAY_MS) + 1;
}

/**
 * The daily medians as a line, or the reason there is no line.
 *
 * A day with no loads produces no point, so the series can be shorter than the
 * span it covers. `MiniSparkline` takes numbers and cannot hold a gap open, so
 * the missing days are counted and said in words instead of being invented as
 * zeros — the FR regression has no 15 or 16 August because that was a weekend,
 * and a line that drew them as 0 ms would show it healing every Saturday.
 */
function readTrend(trend: FoConsoleFindingTrendPoint[]): TrendRead {
  const [first] = trend;
  const last = trend.at(-1);
  if (first == null || last == null) {
    return {
      state: "none",
      caption: `No day in the last ${TREND_DAYS} carried a load in this cell — measured, and nothing there.`,
    };
  }
  if (trend.length < 2) {
    return {
      state: "none",
      caption: `One day measured: ${formatDurationMs(first.p50_ms)} on ${formatDay(first.day)}, over ${formatCount(first.events)} loads. A single point is not a trend, so no line is drawn.`,
    };
  }

  const span = daySpan(first.day, last.day, trend.length);
  const gaps = Math.max(0, span - trend.length);
  const peak = trend.reduce((worst, point) => (point.p50_ms > worst.p50_ms ? point : worst), first);
  const trough = trend.reduce((best, point) => (point.p50_ms < best.p50_ms ? point : best), first);
  const gapNote =
    gaps === 0
      ? ""
      : ` ${gaps === 1 ? "One day" : `${formatCount(gaps)} days`} in that span carried no load at all and ${gaps === 1 ? "is" : "are"} absent from the line rather than drawn as zero.`;

  return {
    state: "line",
    values: trend.map((point) => point.p50_ms),
    short: `${formatDurationMs(first.p50_ms)} → ${formatDurationMs(last.p50_ms)}`,
    label: `Daily median load time, oldest first: ${formatCount(trend.length)} days measured across the ${formatCount(span)} days to ${formatDay(last.day)}, from ${formatDurationMs(first.p50_ms)} on ${formatDay(first.day)} to ${formatDurationMs(last.p50_ms)} on ${formatDay(last.day)}; highest ${formatDurationMs(peak.p50_ms)} on ${formatDay(peak.day)}, lowest ${formatDurationMs(trough.p50_ms)} on ${formatDay(trough.day)}.${gapNote}`,
    caption: `Daily median, oldest first — ${formatCount(trend.length)} of the ${formatCount(span)} days to ${formatDay(last.day)}.${gapNote} The line is ${TREND_DAYS} days whatever window the board is on, so this window can be read as unusual or as normal.`,
  };
}

/** A signal's small heading, identical wherever the signal appears. */
function SignalLabel({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" tone="muted" as="span" className="font-medium">
      {children}
    </Typography>
  );
}

/** The card's trend block: the line, its endpoints, and what it does not cover. */
function TrendBlock({ finding }: { finding: FoConsolePinpointFinding }) {
  if (finding.trend == null) {
    return (
      <div className="flex flex-col gap-1">
        <SignalLabel>{TREND_LABEL}</SignalLabel>
        <Typography variant="body-sm" tone="muted">
          {trendAbsence(pinpointKind(finding.kind)).long}
        </Typography>
      </div>
    );
  }

  const read = readTrend(finding.trend);
  return (
    <div className="flex flex-col gap-1">
      <SignalLabel>{TREND_LABEL}</SignalLabel>
      {read.state === "line" ? (
        <div className="flex items-center gap-2">
          {/* Neutral, not warning: the backend has already judged this finding
              and its kind chip says so. A line that painted its own slope would
              be a second, unmeasured verdict. */}
          <MiniSparkline
            data={read.values}
            tone="neutral"
            endMarker="dot"
            width={132}
            height={28}
            aria-label={read.label}
          />
          <Typography variant="body-sm" as="span" className="tabular-nums">
            {read.short}
          </Typography>
        </div>
      ) : null}
      <Typography variant="caption" tone="muted">
        {read.caption}
      </Typography>
    </div>
  );
}

/** The card's concentration block: the sentence, the loads split, the correction. */
function ConcentrationBlock({ finding }: { finding: FoConsolePinpointFinding }) {
  if (finding.concentration == null) {
    return (
      <div className="flex flex-col gap-1">
        <SignalLabel>{CONCENTRATION_LABEL}</SignalLabel>
        <Typography variant="body-sm" tone="muted">
          {concentrationAbsence(pinpointKind(finding.kind)).long}
        </Typography>
      </div>
    );
  }

  const read = readConcentration(finding.concentration);
  return (
    <div className="flex flex-col gap-1.5">
      <SignalLabel>{CONCENTRATION_LABEL}</SignalLabel>
      <Typography
        variant="body-sm"
        as="p"
        className={cn("tabular-nums", read.shape === "minority" && "font-semibold")}
      >
        {read.headline}
      </Typography>
      {read.segments ? (
        <SegmentBar
          size="sm"
          showLegend
          segments={read.segments}
          aria-label={read.barLabel}
          title={read.barLabel}
        />
      ) : null}
      <Typography variant="caption" tone="muted">
        {read.detail}
      </Typography>
    </div>
  );
}

/** The row's trend: the same line at row scale, or the reason there is none. */
function TrendCell({ finding }: { finding: FoConsolePinpointFinding }) {
  const read = finding.trend == null ? null : readTrend(finding.trend);

  if (read?.state !== "line") {
    const reason = read ? read.caption : trendAbsence(pinpointKind(finding.kind)).long;
    return (
      // `role="img"` so the reason is what a screen reader hears; the bare
      // glyph would be read out as punctuation, which is not the point being
      // made. The em dash is this board's "nothing to show", never a zero.
      <span
        role="img"
        className="text-fg-muted inline-flex w-14 shrink-0 justify-center text-xs"
        title={`${TREND_LABEL}: ${reason}`}
        aria-label={`${TREND_LABEL}: ${reason}`}
      >
        {EM_DASH}
      </span>
    );
  }

  return (
    <span className="inline-flex w-14 shrink-0 justify-center" title={read.label}>
      <MiniSparkline
        data={read.values}
        tone="neutral"
        endMarker="dot"
        width={56}
        height={16}
        aria-label={read.label}
      />
    </span>
  );
}

/**
 * The row's concentration line.
 *
 * It gets its own line under the title rather than a slot in the row, because
 * a sentence that changes what the numbers on the line above mean cannot be the
 * seventh item competing for width.
 */
function ConcentrationLine({ finding }: { finding: FoConsolePinpointFinding }) {
  if (finding.concentration == null) {
    const absence = concentrationAbsence(pinpointKind(finding.kind));
    return (
      <Typography variant="caption" tone="muted" as="span" title={absence.long}>
        {CONCENTRATION_LABEL}: {absence.short}
      </Typography>
    );
  }

  const read = readConcentration(finding.concentration);
  return (
    <>
      <Typography variant="caption" tone="muted" as="span" title={read.detail}>
        <span className="text-fg-muted">{CONCENTRATION_LABEL}: </span>
        <span className={cn("text-fg tabular-nums", read.shape === "minority" && "font-semibold")}>
          {read.headline}
        </span>
      </Typography>
      {read.segments ? (
        <SegmentBar
          size="sm"
          className="w-24 shrink-0"
          segments={read.segments}
          aria-label={read.barLabel}
          title={read.barLabel}
        />
      ) : null}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="text-fg tabular-nums">{value === "" ? EM_DASH : value}</dd>
    </div>
  );
}
